export const dayIndex = {
    Jan: 31,
    Feb: 28,
    Mar: 31,
    Apr: 30,
    May: 31,
    Jun: 30,
    Jul: 31,
    Aug: 31,
    Sep: 30,
    Oct: 31,
    Nov: 30,
    Dec: 31,
};

export const months = Object.keys(dayIndex).map((month) => {
    return { value: month, label: month };
});

export const monthList = months.map((month) => {
    return month.label;
});
export const days = Object.entries(dayIndex).reduce((acc, month, ind) => {
    let daysPerMonth = [];
    for (let i = 1; i < month[1] + 1; i++) {
        daysPerMonth.push({ value: i, label: i });
    }
    return { ...acc, [`${month[0]}`]: daysPerMonth };
}, {});

export const tables = ["preUpgrade"];
// "postUpgrade"

export const rows = months;

export const getYear = (ind, firstMonth, firstYear) => {
    const year = monthList.indexOf(firstMonth) + ind > 11 ? firstYear + 1 : firstYear;
    return year;
};

export const getMonth = (ind, firstMonth) => {
    if (monthList.indexOf(firstMonth) + ind > 11) {
        return `${monthList[monthList.indexOf(firstMonth) + ind - 12]}`;
    } else {
        return monthList[monthList.indexOf(firstMonth) + ind];
    }
};

export const combineUtilitySimulatedData = (utilityData, simData, isUtilityDataPMF = false) => {
    //The overall function that combines two datasets
    //The primary use case assumes utilityData is an hourly array of real-world utility (green button) data
    //and simData is hourly heat pump simulation data
    //Both arrays should be in the form:
    // [
    //     { value: 1.51, temp: 6.3 },
    //     { value: 1.32, temp: 6.9 },
    //     ...
    // ]

    // --- Set parameters ---
    const elecBinWidth = 0.5; //kWh
    const maxEleckWh = 24; //kWh

    //Bins centered on 0
    // const overallTempMin = -37.5; //overall min temp
    // const overallTempMax = 37.5; //overall max temp

    //Bins centered by edges
    const overallTempMin = -40; //overall min temp
    const overallTempMax = 40; //overall max temp

    const tempBinWidth = 5; //˚C

    const numTempBins = Math.ceil((overallTempMax - overallTempMin) / tempBinWidth);

    // console.log("numTempBins", numTempBins);
    //Iterate through the temperature bins
    let temperatureBinCenters = [];
    let combinedElecBinCenters = [];
    const combinedLoads = new Array(numTempBins).fill([]).map((_, tInd) => {
        const tempMin = overallTempMin + tempBinWidth * tInd;
        const tempMax = overallTempMin + tempBinWidth * (tInd + 1);
        const binCenter = tempMin + (tempMax - tempMin) / 2;
        temperatureBinCenters.push(binCenter);

        //Build PMFs for both datasets within temp range (filtering handled within)
        //Utility data we pass may already be in PMF format, ready to be destructured here.
        const {
            pmf: basePMF,
            binCenters: baseBins,
            filteredFraction: baseFilteredFraction,
        } = isUtilityDataPMF
            ? utilityData
            : createHistogramPMF(utilityData, {
                  tempMin,
                  tempMax,
                  binWidth: elecBinWidth,
                  maxValue: maxEleckWh,
              });
        //logging filtered fraction for simulated data because it's always one year
        const {
            pmf: simPMF,
            binCenters: simBins,
            filteredFraction,
        } = createHistogramPMF(simData, {
            tempMin,
            tempMax,
            binWidth: elecBinWidth,
            maxValue: maxEleckWh,
        });

        // console.log(
        //     `range and midpoint: [${tempMin}-${tempMax}), ${binCenter} | filtered: ${baseFilteredFraction.toFixed(
        //         3
        //     )}, ${filteredFraction.toFixed(3)}`
        // );

        const combinedPMF = convolve(basePMF, simPMF);
        //Note that this is the same everytime because these bins are based on the electricity distribution
        combinedElecBinCenters = Array.from(
            { length: combinedPMF.length },
            (_, i) => baseBins[0] + simBins[0] + i * elecBinWidth
        );

        const baseBoxplotParams = computeBoxplotStats(basePMF, baseBins);
        const simBoxplotParams = computeBoxplotStats(simPMF, simBins);
        const combinedBoxplotParams = computeBoxplotStats(combinedPMF, combinedElecBinCenters);

        return {
            tempMin,
            tempMax,
            binCenter,
            filteredFraction,
            basePMF,
            simPMF,
            combinedPMF,
            baseBoxplotParams,
            simBoxplotParams,
            combinedBoxplotParams,
            combinedElecBinCenters,
            baseElecBinCenters: baseBins,
        };
    });

    return combinedLoads;
};

export const getPercentile = (dataArray, percentile) => {
    // dataArray = [1.2, 1.0, ...]
    if (dataArray.length === 0) return null; // no data available
    const sorted = [...dataArray].sort((a, b) => a - b); // sort array in ascending order
    const index = (sorted.length - 1) * percentile; // calculate index for given percentile
    const lower = Math.floor(index); // lower index
    const upper = Math.ceil(index); // upper index
    if (lower === upper) return sorted[lower]; // if same, return value at index
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower); // interpolate between values
};

// const applyACSubtractionFlat = (utilityHourly, baseKWhPerHour, balancePointC = 16) => {
//   // For temps above balance point, replace observed with the flat baseload (i.e., remove AC signal)
//   // For temps at/below the balance point, keep observed (no AC there).
//     return utilityHourly.map(({ value, temp }) => {
//         if (temp == null) return { value, temp }; // leave untouched if no temp
//         if (temp > balancePointC) {
//             // use flat baseload only (no AC component)
//             return { value: baseKWhPerHour, temp };
//         }
//         return { value, temp };
//     });
// };

export const calculateBaseLoad = (hourlyData) => {
    // hourlyData = [
    //   { value: 1.2, temp: 14.1 },
    //   { value: 1.0, temp: 15.2 },
    // ];

    // Step 1: Bin hourly data at 1C wide temperature bins from 14C-18C
    const binCenters = [14, 15, 16, 17, 18]; // required bin centers
    const binWidth = 1; // required bin width

    const baseLoadValues = [];
    binCenters.forEach((center) => {
        const tempMin = center - binWidth / 2; // calculate minimum temp of bin
        const tempMax = center + binWidth / 2; // calculate maximum temp of bin

        // filter data to match bin range
        const binnedValues = hourlyData
            .filter(({ temp, value }) => value != null && temp != null && temp >= tempMin && temp < tempMax)
            .map(({ value }) => value); // extract values in the bin

        // Step 2.1: Take a given percentile from each bin (e.g., 10th percentile)
        const tenthPercentile = getPercentile(binnedValues, 0.1); // 10th percentile of binned value
        if (tenthPercentile != null) baseLoadValues.push(tenthPercentile); // push to array
    });

    // fallback to 0 if no valid base load values were found
    if (baseLoadValues.length === 0) {
        return {
            avgBaseLoad: 0,
            baseLoadValuesPerBin: [],
        };
    }

    // Step 2.2: Average the 10th percentile binned values.
    // NOTE: This result is the average base load (consumption) of the building.
    //       However, this does not reflect avg. temperature-independent consumption
    //       which could be calculated via the mean or median energy consumption of
    //       the home at 14-18C.
    const averageBaseLoad = baseLoadValues.reduce((sum, val) => sum + val, 0) / baseLoadValues.length; // calculate average base load

    return {
        avgBaseLoad: averageBaseLoad, // returns the average base load (consumption) across all bins
        baseLoadValuesPerBin: baseLoadValues, // returns ALL base load values by bin
    };
};

export const getMeanAndMedianEnergyConsumption = (hourlyData, lowerLimit, upperLimit) => {
    // hourlyData = [
    //   { value: 1.2, temp: 14.1 },
    //   { value: 1.0, temp: 15.2 },
    // ];

    const filteredData = hourlyData
        .filter(({ value, temp }) => temp != null && value != null && temp >= lowerLimit && temp <= upperLimit)
        .map(({ value }) => value); // extract values within the specified range
    if (filteredData.length === 0) return { mean: null, median: null };

    // calculate mean
    const mean = filteredData.reduce((sum, val) => sum + val, 0) / filteredData.length;

    // calculate median
    const sortedData = [...filteredData].sort((a, b) => a - b); // sort ascending order
    const mid = Math.floor(sortedData.length / 2); // find middle
    const median = sortedData.length % 2 !== 0 ? sortedData[mid] : (sortedData[mid - 1] + sortedData[mid]) / 2; // odd: return middle value; even: return average of two middle values

    return { mean, median };
};

// HELPER FUNCTIONS
export const createHistogramPMF = (data, { tempMin, tempMax, binWidth, minValue = 0, maxValue = 48 }) => {
    //Creates a histogram probability mass function for an array of data in the form of:
    // [
    //     { value: 1.51, temp: 6.3 },
    //     { value: 1.32, temp: 6.9 },
    //     ...
    // ]
    // This function returns ONE histogram at a temperature bin, from [tempMin-tempMax],
    // Bin width is the width of the kWh bin.
    // Default max value is 48 kW = 200 Amps
    // Doesn't support negative power flow (solar PV) at the moment
    // This function must be called for every temperature bin if looking to generate PMFs across an entire dataset
    // Corresponding bins will be:
    // [0.0 - 1*binWidth), [1*binWidth - 2*binWidth), [2*binWidth - 3*binWidth), ... [(numBins-1)*binWidth - numBins*binWidth)

    //Filter the data for the current temperature bin
    const filtered = data
        .filter((d) => {
            const { value, temp } = d;
            return !Number.isNaN(value) && value != null && !Number.isNaN(temp) && temp != null;
        })
        .filter((d) => d.temp >= tempMin && d.temp < tempMax);

    // console.log("data vs filter", data.length, filtered.length);
    // console.log("maxValue - minValue", maxValue, minValue);

    const numBins = Math.ceil((maxValue - minValue) / binWidth);

    const histogram = new Array(numBins).fill(0);

    //increments the observed count at each bin
    filtered.forEach(({ value }) => {
        if (value >= minValue && value <= maxValue) {
            const binIndex = Math.floor(value / binWidth);
            histogram[binIndex]++;
        }
    });

    const total = histogram.reduce((sum, count) => sum + count, 0);
    const pmf = total > 0 ? histogram.map((count) => count / total) : histogram;

    // Compute bin centers
    const binCenters = Array.from({ length: numBins }, (_, i) => (i + 0.5) * binWidth);

    return { pmf, binCenters, filteredFraction: filtered.length / data.length };
};

//exports.createHistogramPMF = createHistogramPMF;

// normalizes histograms
export const normalize = (hist) => {
    const total = hist.reduce((sum, val) => sum + val, 0);

    if (total === 0) return hist;

    return hist.map((val) => val / total);
};

// Performs discrete convolution between histograms a and b
export const convolve = (a, b) => {
    const aNorm = normalize(a);
    const bNorm = normalize(b);
    const result = new Array(a.length + b.length - 1).fill(0);

    for (let i = 0; i < aNorm.length; i++) {
        for (let j = 0; j < bNorm.length; j++) {
            result[i + j] += aNorm[i] * bNorm[j];
        }
    }

    return normalize(result);
};

export const computeBoxplotStats = (pmf, binCenters) => {
    const n = pmf.length;

    if (pmf.every((el) => el === 0)) {
        return {
            mean: 0,
            median: 0,
            stdDev: 0,
            variance: 0,
            q1: 0,
            q3: 0,
            iqr: 0,
            min: 0,
            max: 0,
            n: n || 0,
            emptyBin: true,
        };
    }

    const cumulative = [];
    let cumSum = 0;

    // Cumulative distribution function
    for (let i = 0; i < n; i++) {
        cumSum += pmf[i];
        cumulative.push(cumSum);
    }

    // Mean
    const mean = pmf.reduce((acc, p, i) => acc + p * binCenters[i], 0);

    // Variance and standard deviation
    const variance = pmf.reduce((acc, p, i) => acc + p * Math.pow(binCenters[i] - mean, 2), 0);
    const stdDev = Math.sqrt(variance);

    // Quantile helper
    const quantile = (q) => {
        for (let i = 0; i < cumulative.length; i++) {
            if (cumulative[i] >= q) return binCenters[i];
        }
        return binCenters[binCenters.length - 1];
    };

    const median = quantile(0.5);
    const q1 = quantile(0.25);
    const q3 = quantile(0.75);
    const iqr = q3 - q1;

    // Min and Max where probability is nonzero
    const min = binCenters[pmf.findIndex((p) => p > 0)];
    const max = binCenters[pmf.length - 1 - [...pmf].reverse().findIndex((p) => p > 0)];

    return {
        mean: mean === undefined ? null : mean,
        median: median === undefined ? null : median,
        stdDev: stdDev === undefined ? null : stdDev,
        variance: variance === undefined ? null : variance,
        q1: q1 === undefined ? null : q1,
        q3: q3 === undefined ? null : q3,
        iqr: iqr === undefined ? null : iqr,
        min: min === undefined ? null : min,
        max: max === undefined ? null : max,
        n: n || 0,
        emptyBin: false,
    };
};
